/*
 * Hoysala Coffee & Spices — client runtime.
 *
 * Reimplements, in plain JS, the behaviour the original React build supplied:
 * scroll reveals, the satellite zoom, animated stats, the export map, the swipe
 * carousels, the coffee reel, the certification switcher and the interest list.
 * Timings and easings are copied from the original components, not eyeballed.
 */
(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const BASE = window.HOYSALA_BASE || '';

  // The only way to reach the estate from the site. Never printed on a page —
  // it is a mailto: target only (Rahul, 22 Sep 2026).
  const CONTACT_EMAIL = 'hoysalacosco@gmail.com';

  /**
   * Horizontal swipe on a cross-fading stage.
   *
   * The satellite frames and the coffee reel are stacked and cross-faded rather
   * than laid out in a row, so there is no scroll position for native
   * scroll-snap to work with — the gesture has to be read directly. Returns a
   * direction: 1 for a leftward swipe (next), -1 for rightward (previous).
   *
   * Vertical-dominant gestures are ignored so swiping the page up and down
   * still scrolls normally over the top of these.
   */
  const onSwipe = (el, handler) => {
    let x0 = null;
    let y0 = null;
    const start = (e) => {
      const p = e.touches ? e.touches[0] : e;
      x0 = p.clientX;
      y0 = p.clientY;
    };
    const end = (e) => {
      if (x0 === null) return;
      const p = e.changedTouches ? e.changedTouches[0] : e;
      const dx = p.clientX - x0;
      const dy = p.clientY - y0;
      x0 = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      handler(dx < 0 ? 1 : -1);
    };
    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchend', end, { passive: true });
    // Mouse and pen only — a touch would already have been handled above, and
    // binding both would advance two frames per swipe.
    el.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'touch') start(e); });
    el.addEventListener('pointerup', (e) => { if (e.pointerType !== 'touch') end(e); });
    el.style.touchAction = 'pan-y';
  };

  // Every gallery on the site shipped one of two versions of the same mistake.
  // The dot strip under the picture was the only control that worked, and each
  // dot is a 6px square — a target nobody aims at with a mouse and nobody can
  // hit with a thumb. Where arrows existed at all they were overlaid on the
  // photograph at opacity-0 until :hover, so they were invisible on a phone,
  // and on a desktop invisible until the pointer was already on the picture.
  // Between them that left drag-the-image as the discoverable way to move a
  // carousel, which is not a thing anyone tries.
  //
  // This builds the one navigator they now all share: an arrow either side of
  // the strip, always visible, every target 36px. It replaces the contents of
  // the strip's existing wrapper, so nothing moves on the page.
  //
  // `labels` names each stop for a screen reader. `opts.variant` picks the
  // strip's look — 'dots' (round, widens when current) or 'bars' (the wider
  // segments the satellite zoom uses to show depth) — and `opts.accent` its
  // colour, so each section keeps the look it already had. Returns paint(),
  // leaving the caller's own show()/update() in charge of state.
  const buildReelNav = (wrap, labels, handlers, opts = {}) => {
    if (!wrap || labels.length < 2) return { paint: () => {} };

    const arrow = (label, glyph, onClick) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hoysala-reel-arrow';
      b.setAttribute('aria-label', label);
      b.textContent = glyph;
      b.addEventListener('click', onClick);
      return b;
    };

    const noun = opts.noun || 'picture';
    wrap.removeAttribute('aria-hidden');
    wrap.className =
      'hoysala-reel-nav' +
      (opts.variant === 'bars' ? ' hoysala-reel-nav--bars' : '') +
      (opts.accent === 'amber' ? ' hoysala-reel-nav--amber' : '');
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', opts.label || `Move through the ${noun}s`);
    wrap.textContent = '';

    wrap.appendChild(arrow(`Previous ${noun}`, '←', () => handlers.prev()));
    // The strip sits in its own box so it can wrap to a second line — the home
    // page's process carousel has nine slides — without carrying the arrows
    // away from the ends of the row with it.
    const strip = document.createElement('div');
    strip.className = 'hoysala-reel-strip';
    const dots = labels.map((text, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hoysala-reel-dot';
      b.setAttribute('aria-label', text);
      b.appendChild(Object.assign(document.createElement('span'), { className: 'hoysala-reel-dot-bar' }));
      b.addEventListener('click', () => handlers.go(i));
      strip.appendChild(b);
      return b;
    });
    wrap.appendChild(strip);
    wrap.appendChild(arrow(`Next ${noun}`, '→', () => handlers.next()));

    // Left/right keys once focus is anywhere in the row.
    wrap.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      if (e.key === 'ArrowRight') handlers.next(); else handlers.prev();
    });

    return { paint: (n) => dots.forEach((d, i) => d.setAttribute('aria-current', String(i === n))) };
  };

  // Resolve an image filename to a URL. The static site can just prefix
  // assets/images/, but Shopify serves assets from a CDN path carrying a
  // per-file version query, which cannot be reconstructed by concatenation —
  // so the theme injects an explicit filename -> URL map. Falls back to the
  // static layout, leaving the plain site unchanged.
  const assetUrl = (name) =>
    (window.HOYSALA_ASSETS && window.HOYSALA_ASSETS[name]) || `${BASE}assets/images/${name}`;

  // Resolve a site path such as "coffee/athi" to a URL. Shopify can't serve the
  // nested paths the static site uses, so the theme collapses every page to
  // /pages/<path-with-hyphens> and sets HOYSALA_PAGES. Falls back to the static
  // layout, leaving the plain site unchanged.
  // --- back control --------------------------------------------------------
  // Returns the visitor to the previous page AT THE SCROLL POSITION THEY LEFT.
  // history.back() is what makes that work: the browser restores scroll itself
  // (and serves the page from bfcache where it can), which no manual
  // save-and-restore of scrollY can match for accuracy.
  //
  // Only intercepts when there is same-origin history to go back to. Someone
  // arriving from a shared link or a search result follows the href instead,
  // so the control is never a dead end.
  if ('scrollRestoration' in history) history.scrollRestoration = 'auto';

  document.querySelectorAll('[data-back]').forEach((el) => {
    el.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      let sameOrigin = false;
      try {
        sameOrigin = !!document.referrer && new URL(document.referrer).origin === location.origin;
      } catch (_) {
        sameOrigin = false;
      }

      if (sameOrigin && history.length > 1) {
        event.preventDefault();
        history.back();
      }
    });
  });

  const pageUrl = (sitePath) => {
    const clean = String(sitePath).replace(/^\/+|\/+$/g, '');
    return window.HOYSALA_PAGES
      ? `${window.HOYSALA_PAGES}${clean.replace(/\//g, '-')}`
      : `${BASE}${clean}/index.html`;
  };
  const HOME = (() => {
    const el = document.getElementById('hoysala-home-data');
    return el ? JSON.parse(el.textContent) : null;
  })();

  /* ------------------------------------------------------------ 1. reveals */
  // useReveal(): threshold .15, rootMargin '0px 0px -60px 0px', fires once.
  const revealObserver = new IntersectionObserver(
    (entries, obs) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('reveal-in');
        obs.unobserve(e.target);
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -60px 0px' },
  );
  const observeReveals = (ctx = document) => $$('.reveal', ctx).forEach((el) => revealObserver.observe(el));

  /* -------------------------------------------------------- 2. mobile menu */
  (() => {
    const toggle = $('[data-menu-toggle]');
    const panel = $('[data-menu-panel]');
    if (!toggle || !panel) return;
    const bars = $$('span > span', toggle);
    let open = false;
    const paint = () => {
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      panel.classList.toggle('max-h-0', !open);
      panel.classList.toggle('max-h-[80vh]', open);
      if (bars.length === 3) {
        bars[0].className = `absolute left-0 right-0 h-px bg-current transition-all ${open ? 'top-2 rotate-45' : 'top-0'}`;
        bars[1].className = `absolute left-0 right-0 top-2 h-px bg-current transition-opacity ${open ? 'opacity-0' : 'opacity-100'}`;
        bars[2].className = `absolute left-0 right-0 h-px bg-current transition-all ${open ? 'top-2 -rotate-45' : 'top-4'}`;
      }
    };
    toggle.addEventListener('click', () => { open = !open; paint(); });
    paint();
  })();

  /* ----------------------------------------------------- 3. satellite zoom */
  (() => {
    const stage = $('[data-satellite-stage]');
    if (!stage || !HOME) return;
    const frames = $$(':scope > .transition-opacity', stage);
    // The label and its line of description used to be a translucent slab over
    // the bottom fifth of the frame, which is the part of these maps carrying
    // the Angadi locator. They now sit under the map instead — same two lines,
    // same order, nothing over the artwork.
    const bar = $('[data-satellite-caption]', stage.parentElement);
    if (!frames.length) return;

    // Same navigator as the galleries, keeping this section's wider segments:
    // they read as zoom depth rather than as a set of alternatives, and four
    // round dots would lose that. The 6px height was the problem, not the shape.
    const stripWrap = stage.parentElement.querySelector('.mt-5');
    const nav = buildReelNav(
      stripWrap,
      $$('button', stripWrap || document.createDocumentFragment()).map(
        (b, i) => b.getAttribute('aria-label') || (HOME.satellite[i] && HOME.satellite[i].label) || `View ${i + 1}`,
      ),
      { prev: () => goTo(index - 1), next: () => goTo(index + 1), go: (i) => goTo(i) },
      { variant: 'bars', noun: 'view', label: 'Zoom in on the estate' },
    );

    let index = 0;
    const show = (n) => {
      index = n;
      frames.forEach((f, i) => {
        f.style.opacity = i === n ? '1' : '0';
        f.setAttribute('aria-hidden', String(i !== n));
        const img = $('img', f);
        if (img) img.style.transform = i === n ? 'scale(1.12)' : 'scale(1)';
      });
      const meta = HOME.satellite[n];
      if (bar && meta) {
        bar.children[0].textContent = meta.label;
        bar.children[1].textContent = meta.caption;
      }
      nav.paint(n);
    };
    // The auto-advance is restarted after any manual change, so a swipe is not
    // immediately overridden by a timer that was already most of the way through.
    let timer = 0;
    const autoplay = () => {
      clearInterval(timer);
      timer = setInterval(() => show((index + 1) % frames.length), HOME.satelliteIntervalMs);
    };
    const goTo = (n) => { show((n + frames.length) % frames.length); autoplay(); };

    onSwipe(stage, (dir) => goTo(index + dir));
    // The SSR painted the first frame, so nothing used to call show() until the
    // first autoplay tick. The strip is built here now rather than served, so
    // it needs that first paint or it starts with no segment marked.
    //
    // It also painted that frame at scale(1.12) — already zoomed in — so the
    // page opened on an India cropped by 12%, with the top and bottom of the
    // country outside the frame, and never zoomed because it was there
    // already. Frames 2-4 do zoom: they sit at scale(1) while hidden, so
    // show() moving them to 1.12 runs the 4.2s transition. Putting frame 1
    // back to scale(1) and committing that (the offsetWidth read below forces
    // the reflow, without which both writes coalesce into one and no
    // transition runs) gives the opening frame the same move: the whole map
    // first, then the zoom in.
    frames.forEach((f) => {
      const img = $('img', f);
      if (img) img.style.transform = 'scale(1)';
    });
    void stage.offsetWidth;
    show(0);
    autoplay();
  })();

  /* ------------------------------------------------- 4. stats + cert badges */
  (() => {
    const statsEl = $('[data-stats]');
    if (!statsEl || !HOME) return;
    const items = $$(':scope > div', statsEl);
    const badges = $$(':scope > a', $('[data-badges]') || document.createDocumentFragment());
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const countTo = (numEl, target) => {
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / 1400, 1);
        const value = Math.round(target * easeOutCubic(p)).toLocaleString();
        if (numEl.firstChild && numEl.firstChild.nodeType === 3) numEl.firstChild.nodeValue = value;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const run = () => {
      items.forEach((el, i) => {
        el.style.transitionDelay = `${i * 150}ms`;
        el.style.opacity = '1';
        el.style.transform = 'none';
        const numEl = $('.tabular-nums', el);
        const target = HOME.stats[i] && HOME.stats[i].to;
        if (numEl && target != null) countTo(numEl, target);
      });
      badges.forEach((el, i) => {
        el.style.transitionDelay = `${300 + i * 150}ms`;
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    };

    const obs = new IntersectionObserver((entries, o) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        run();
        o.disconnect();
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    obs.observe(statsEl);
  })();

  /* ---------------------------------------------------------- 5. world map */
  (() => {
    const tabs = $('[data-map-tabs]');
    if (!tabs || !HOME) return;
    const M = HOME.map;
    const stage = tabs.nextElementSibling;
    if (!stage) return;

    let active = 'india';
    let mounted = false;

    const tabClass = (on) =>
      'whitespace-nowrap rounded-full px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] transition-colors sm:px-4 sm:py-2 sm:text-xs sm:tracking-[0.14em] ' +
      (on ? 'bg-forest text-paper' : 'border border-line bg-paper text-forest hover:border-amber hover:text-amber');

    const callout = () =>
      '<div class="pointer-events-none absolute" style="left:48.5%;top:63.5%;opacity:1;transition:opacity 600ms ease 900ms">' +
        '<span class="absolute -translate-x-1/2 -translate-y-1/2"><span class="relative block h-4 w-4">' +
          '<span class="absolute inset-0 rounded-full border-2 border-amber"></span>' +
          '<span class="absolute inset-[3px] rounded-full border border-paper bg-forest"></span>' +
        '</span></span>' +
        '<span class="absolute right-4 top-1/2 h-px w-14 bg-amber" style="transform:translateY(-50%)"></span>' +
        '<span class="absolute right-[4.75rem] top-1/2 -translate-y-1/2 whitespace-nowrap text-right">' +
          '<span class="block text-[0.6rem] font-bold uppercase tracking-[0.18em] text-amber">Chikmagalur</span>' +
          '<span class="block font-display text-sm text-forest">Our Farms</span>' +
        '</span></div>';

    const render = () => {
      const cities = M.regions[active] || [];
      const mask = assetUrl(M.masks[active]);
      stage.className = `relative mx-auto mt-8 w-full max-w-2xl ${M.ratios[active]}`;

      const maskStyle =
        `mask-image:url(${mask});-webkit-mask-image:url(${mask});` +
        'mask-repeat:no-repeat;-webkit-mask-repeat:no-repeat;' +
        'mask-position:center;-webkit-mask-position:center;' +
        'mask-size:contain;-webkit-mask-size:contain';

      let html = `<div role="img" aria-label="${M.labels[active]}" class="h-full w-full bg-forest" style="${maskStyle}"></div>`;
      cities.forEach((c, i) => {
        const name = c.country ? `${c.city}, ${c.country}` : c.city;
        html +=
          `<button type="button" aria-label="${name}" data-city="${c.city}" class="absolute z-20 -translate-x-1/2 -translate-y-1/2 p-3 focus:outline-none" style="left:${c.x}%;top:${c.y}%">` +
            `<span class="relative block h-[9px] w-[9px]" style="opacity:${mounted ? 1 : 0};transform:scale(${mounted ? 1 : 0.4});transition:opacity 400ms ease ${i * 70}ms, transform 400ms ease ${i * 70}ms">` +
              `<span aria-hidden="true" class="absolute inset-0 rounded-full bg-amber animate-ping" style="animation-duration:2.6s;animation-delay:${(i % 5) * 400}ms;opacity:.55"></span>` +
              '<span data-dot class="relative block h-full w-full rounded-full border border-paper transition-transform duration-200 bg-amber"></span>' +
            '</span>' +
            `<span data-tip class="absolute left-1/2 top-full z-30 -mt-1 -translate-x-1/2 whitespace-nowrap rounded-full bg-forest px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-paper shadow-sm" style="opacity:0;transform:translate(-50%, -4px);transition:opacity 200ms ease, transform 200ms ease;pointer-events:none">${name}</span>` +
          '</button>';
      });
      if (active === 'india') html += callout();
      stage.innerHTML = html;

      // hover/focus state is toggled in place so the ping animation never restarts
      $$('button[data-city]', stage).forEach((btn) => {
        const dot = $('[data-dot]', btn);
        const tip = $('[data-tip]', btn);
        const set = (on) => {
          dot.className =
            'relative block h-full w-full rounded-full border border-paper transition-transform duration-200 ' +
            (on ? 'bg-forest scale-[1.5]' : 'bg-amber');
          tip.style.opacity = on ? '1' : '0';
          tip.style.transform = `translate(-50%, ${on ? '0' : '-4px'})`;
        };
        btn.addEventListener('mouseenter', () => set(true));
        btn.addEventListener('mouseleave', () => set(false));
        btn.addEventListener('focus', () => set(true));
        btn.addEventListener('blur', () => set(false));
        btn.addEventListener('click', (e) => { e.stopPropagation(); set(tip.style.opacity !== '1'); });
      });

      if (!mounted) {
        mounted = true;
        // only the dot wrapper fades in — :not([data-tip]) keeps the hover
        // tooltip hidden until the pin is actually hovered or focused
        requestAnimationFrame(() => {
          $$('button[data-city] > span:not([data-tip])', stage).forEach((s) => {
            s.style.opacity = '1';
            s.style.transform = 'scale(1)';
          });
        });
      }
    };

    // rebuild the tab row so every region is reachable (SSR shipped India only)
    tabs.innerHTML = M.tabs
      .map((t) => `<button type="button" data-tab="${t.key}" aria-pressed="${t.key === active}" class="${tabClass(t.key === active)}">${t.label}</button>`)
      .join('');
    $$('button[data-tab]', tabs).forEach((btn) => {
      btn.addEventListener('click', () => {
        active = btn.dataset.tab;
        $$('button[data-tab]', tabs).forEach((b) => {
          const on = b.dataset.tab === active;
          b.setAttribute('aria-pressed', String(on));
          b.className = tabClass(on);
        });
        render();
      });
    });
    render();
  })();

  /* ------------------------------------------------------- 6. swipe carousels */
  $$('[data-carousel]').forEach((root) => {
    let slides = [];
    try { slides = JSON.parse(root.dataset.slides || '[]'); } catch { /* keep SSR caption */ }
    const track = $('[role="region"]', root);
    if (!track) return;
    const count = track.children.length;
    const dotWrap = root.children[1];
    const caption = $('p.font-display', root);

    // The only arrow the SSR carries is the forward one, overlaid on the
    // picture at opacity-0 until the carousel is hovered — so on a phone it
    // does not exist, and on a desktop it appears only once the pointer is
    // already where it is needed. It is dropped in favour of the shared
    // navigator below, which is visible from across the page and is the same
    // control the coffee reel uses. Slide labels come from the dots the SSR
    // already labelled correctly.
    $$(':scope > .relative > button', root).forEach((b) => b.remove());
    const labels = dotWrap
      ? $$('button', dotWrap).map((b, i) => b.getAttribute('aria-label') || (slides[i] && slides[i].name) || `Picture ${i + 1}`)
      : [];
    const nav = buildReelNav(
      dotWrap,
      labels,
      { prev: () => go(index - 1), next: () => go(index + 1), go: (i) => go(i) },
      { accent: 'amber', noun: 'picture' },
    );

    let noteEl = $('p.mt-1.text-center.text-xs.text-ink-2', root);
    if (!noteEl && slides.some((s) => s.note) && caption) {
      noteEl = document.createElement('p');
      noteEl.className = 'mt-1 text-center text-xs text-ink-2';
      caption.insertAdjacentElement('afterend', noteEl);
    }
    let detailEl = $('div.mt-4.mx-auto.max-w-xl', root);
    if (!detailEl && slides.some((s) => s.detail) && caption) {
      detailEl = document.createElement('div');
      detailEl.className = 'mt-4 mx-auto max-w-xl';
      (noteEl || caption).insertAdjacentElement('afterend', detailEl);
    }

    let index = 0;
    let programmatic = false;
    let releaseTimer = null;

    const update = (i) => {
      nav.paint(i);
      const s = slides[i];
      if (s && caption) caption.textContent = s.name;
      if (noteEl) {
        noteEl.textContent = (s && s.note) || '';
        noteEl.hidden = !(s && s.note);
      }
      if (detailEl) {
        detailEl.innerHTML = (s && s.detail) || '';
        detailEl.hidden = !(s && s.detail);
      }
    };

    // Wraps rather than stopping at the ends. The old arrows were removed from
    // the run at index 0 and index count-1, which is fine for a control that is
    // only visible on hover but not for one that is always on screen: a button
    // that vanishes as you reach it reads as broken, and the row would change
    // width underneath the pointer.
    const go = (i) => {
      index = ((i % count) + count) % count;
      programmatic = true;
      track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
      clearTimeout(releaseTimer);
      releaseTimer = setTimeout(() => { programmatic = false; }, 500);
      update(index);
    };

    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!track.clientWidth || programmatic) return;
        const i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== index) { index = i; update(i); }
      });
    }, { passive: true });

    // No onSwipe here, unlike the coffee reel: this track is a real scroll
    // container, so a finger already moves it, and onSwipe sets touch-action
    // pan-y — which would take that away.
    update(0);
  });

  /* --------------------------------------------------- 7. "Our Coffees" reel */
  (() => {
    const pills = $('[data-showcase-pills]');
    if (!pills || !HOME || !HOME.showcase) return;
    const root = pills.parentElement;
    const stage = $('.relative.overflow-hidden', root);
    if (!stage) return;
    const slideEls = Array.from(stage.children);
    const dotWrap = $('.mt-3.flex.justify-center.gap-2', root);
    const caption = $('p.font-display', root);
    const noteEl = $('p.mt-1.text-center.text-xs.text-ink-2', root);
    const ctaWrap = $('p.mt-3.text-center', root);
    const cta = ctaWrap ? $('a', ctaWrap) : null;

    const flat = [];
    HOME.showcase.forEach((cat, ci) => {
      cat.varieties.forEach((v) => flat.push({ ...v, categoryIndex: ci, categoryLabel: cat.label }));
    });
    if (flat.length < 2) return;

    // This reel had it worse than the rest: both of its control rows shipped as
    // <span>s inside aria-hidden wrappers, so the category pills did nothing at
    // all — they only repainted to show which category the current picture
    // belonged to. They become real buttons here; the strip below is the shared
    // navigator.
    const pillEls = Array.from(pills.children).map((span) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = span.className;
      btn.textContent = span.textContent;
      span.replaceWith(btn);
      return btn;
    });
    pills.removeAttribute('aria-hidden');
    pills.setAttribute('role', 'group');
    pills.setAttribute('aria-label', 'Choose a coffee type');

    const nav = buildReelNav(
      dotWrap,
      flat.map((item) => `${item.name} — ${item.note || item.categoryLabel}`),
      { prev: () => step(-1), next: () => step(1), go: (i) => show(i) },
      { noun: 'coffee', label: 'Move through our coffees' },
    );

    let index = 0;
    const show = (n) => {
      index = n;
      const item = flat[n];
      slideEls.forEach((el, i) => {
        el.className = `transition-opacity duration-500 ${i === n ? 'opacity-100' : 'pointer-events-none absolute inset-0 opacity-0'}`;
        el.setAttribute('aria-hidden', String(i !== n));
      });
      nav.paint(n);
      pillEls.forEach((p, i) => {
        const on = i === item.categoryIndex;
        p.className = `whitespace-nowrap rounded-full px-2.5 py-1.5 text-[11px] font-medium transition-colors sm:px-4 sm:py-2 sm:text-sm ${
          on ? 'bg-forest text-paper' : 'border border-line bg-paper text-forest'}`;
        p.setAttribute('aria-pressed', String(on));
      });
      if (caption) caption.textContent = item.name;
      if (noteEl) { noteEl.textContent = item.note || ''; noteEl.hidden = !item.note; }
      if (cta && item.to) cta.setAttribute('href', pageUrl(item.to));
      if (ctaWrap) ctaWrap.hidden = !item.to;
    };
    show(0);

    const step = (dir) => show((index + dir + flat.length) % flat.length);

    // No autoplay here: this reel is a catalogue, and a buyer reading a lot
    // name should not have it changed out from under them. It advances only
    // when the reader asks it to.
    onSwipe(stage, step);

    // A pill selects its category; clicking the one already selected walks
    // through that category's coffees. Every pill click therefore changes
    // something on screen — a pill that highlights and does nothing else is
    // what made these read as labels rather than controls in the first place.
    pillEls.forEach((btn, ci) => {
      const group = [];
      flat.forEach((f, i) => { if (f.categoryIndex === ci) group.push(i); });
      if (!group.length) return;
      btn.addEventListener('click', () => {
        const at = group.indexOf(index);
        show(at < 0 ? group[0] : group[(at + 1) % group.length]);
      });
    });
  })();

  /* ------------------------------------------- 8. certification switcher */
  (() => {
    const panels = $$('[data-cert-panel]');
    if (!panels.length) return;
    const want = new URLSearchParams(location.search).get('cert') || 'conventional';
    let shown = panels.find((p) => p.dataset.certPanel === want) || panels[panels.length - 1];
    panels.forEach((p) => { p.hidden = p !== shown; });
    observeReveals(shown);

    // Demeter / Organic, inside the organic panel. The certifier issues these
    // as two standards but the offer data carries them as one string, so the
    // rows were tagged at build time (see _build/demeter.mjs) and the split
    // happens here. ?sub= makes each view linkable — which is what the two
    // home-page badges now point at, instead of both landing on this panel.
    const subBar = $('[data-cert-sub-bar]', shown);
    if (subBar) {
      const SUBS = ['all', 'demeter', 'organic'];
      const PILL = 'whitespace-nowrap rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] transition-colors sm:px-4 sm:py-2 sm:text-xs sm:tracking-[0.14em] ';
      const ON = 'bg-forest text-paper';
      const OFF = 'border border-line bg-paper text-forest hover:border-amber hover:text-amber';

      const subButtons = $$('[data-cert-sub]', subBar);
      const subRows = $$('[data-cert-sub-row]', shown);
      const subBlocks = $$('[data-cert-sub-block]', shown);
      // one process chip per lot block, in the same order — a chip whose block
      // is filtered out has nothing left to scroll to, so it goes too
      const subChips = $$('.sticky button', shown);

      const applySub = (sub) => {
        // a coffee holding both certificates is tagged "demeter organic" and
        // shows under either filter
        subRows.forEach((r) => { r.hidden = sub !== 'all' && !(r.dataset.certSubRow || '').split(' ').includes(sub); });
        subBlocks.forEach((b, i) => {
          const has = sub === 'all' || (b.dataset.certSubBlock || '').split(' ').includes(sub);
          b.hidden = !has;
          if (subChips[i]) subChips[i].hidden = !has;
        });
        subButtons.forEach((b) => {
          const on = b.dataset.certSub === sub;
          b.setAttribute('aria-pressed', String(on));
          b.className = PILL + (on ? ON : OFF);
        });
      };

      const wantSub = new URLSearchParams(location.search).get('sub');
      applySub(SUBS.includes(wantSub) ? wantSub : 'all');

      subButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const sub = btn.dataset.certSub;
          applySub(sub);
          // keep the address bar in step so the view survives a copied link
          const url = new URL(location.href);
          if (sub === 'all') url.searchParams.delete('sub');
          else url.searchParams.set('sub', sub);
          history.replaceState(null, '', url);
        });
      });
    }

    // sticky process chips jump to their lot block, matching the original offsets
    const jumps = $$('.sticky button', shown);
    const blocks = $$('.scroll-mt-40', shown);
    jumps.forEach((btn, i) => {
      btn.addEventListener('click', () => {
        const el = blocks[i];
        if (!el) return;
        const offset = window.innerWidth < 768 ? 120 : 140;
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
      });
    });

    // Which lot block the reader is standing in. The bar was five identical
    // outline chips with no current state: it said what the list held but
    // never where you were in it, and clicking a chip scrolled you somewhere
    // that looked exactly like where you came from. The chip for the block at
    // the top of the page fills dark green; that block's header takes the
    // light green wash, so the two are visibly the same thing.
    if (jumps.length && blocks.length) {
      let queued = false;
      const mark = () => {
        queued = false;
        // the line the sticky bars rest on — the same offset the jump uses
        const line = (window.innerWidth < 768 ? 120 : 140) + 8;
        let at = -1;
        blocks.forEach((b, i) => {
          if (b.hidden) return;
          if (b.getBoundingClientRect().top <= line) at = i;
        });
        // above the first block: light the first one that a filter has left
        if (at < 0) at = blocks.findIndex((b) => !b.hidden);
        jumps.forEach((btn, i) => {
          const on = i === at;
          btn.setAttribute('aria-pressed', String(on));
          btn.classList.toggle('is-current', on);
        });
        blocks.forEach((b, i) => b.classList.toggle('is-current', i === at));
      };
      // A plain timer rather than requestAnimationFrame: this only repaints a
      // chip and a header, so it does not need to land on a frame boundary,
      // and rAF is throttled to nothing in a background tab — which is where
      // a second tab of the same list sits while a buyer compares lots.
      const queue = () => {
        if (queued) return;
        queued = true;
        setTimeout(mark, 80);
      };
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue);
      // a sub-filter hides blocks, which moves every other one
      $$('[data-cert-sub]', shown).forEach((b) => b.addEventListener('click', queue));
      mark();
    }
  })();

  /* --------------------------------------------------------------- 9. cart */
  //
  // The store was already here and already worked; what was missing was every
  // sign of it. Adding a coffee wrote to localStorage and changed one small
  // chip in a table cell — no count in the header, no route to /enquiry from
  // any page on the site, so the list could be filled but never opened.
  //
  // Three pieces below: the add/remove buttons (as before), the header count,
  // and a toast that names where the coffee went and links there.
  const KEY = 'hoysala.interest-list.v1';
  const readList = () => { try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; } };
  const writeList = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* private mode */ } };

  const ICON_PLUS = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-plus SIZE" aria-hidden="true"><path d="M5 12h14"></path><path d="M12 5v14"></path></svg>';
  const ICON_CHECK = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check SIZE" aria-hidden="true"><path d="M20 6 9 17l-5-5"></path></svg>';
  const ICON_CART = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shopping-cart SIZE" aria-hidden="true"><circle cx="8" cy="21" r="1"></circle><circle cx="19" cy="21" r="1"></circle><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path></svg>';

  /* -- the header count ---------------------------------------------------- */
  // Bumps only when the number actually changes, so the badge is not animating
  // on every page load — a pulse that fires unprompted stops meaning anything.
  let lastCount = null;
  const paintCart = () => {
    const n = readList().length;
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = String(n);
      el.hidden = n === 0;
      if (lastCount !== null && n !== lastCount && n > 0) {
        el.classList.remove('is-bumped');
        void el.offsetWidth; // restart the animation rather than queue it
        el.classList.add('is-bumped');
      }
    });
    // The phone menu row and the offer list's "View Cart" carry the count as
    // text instead of a badge, since neither has an icon to hang one on.
    $$('[data-cart-count-text]').forEach((el) => { el.textContent = n ? ` (${n})` : ''; });
    lastCount = n;
  };

  /* -- the toast ----------------------------------------------------------- */
  let toastEl = null;
  let toastTimer = null;
  const toast = (text, linkText) => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'hoysala-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML =
      `<span>${text}</span>` +
      (linkText ? `<a href="${pageUrl('enquiry')}">${linkText}</a>` : '');
    // Flush the closed state with a forced layout read rather than waiting on
    // requestAnimationFrame: rAF does not run in a background tab, and a toast
    // that only appears once the tab is focused is worse than no toast.
    toastEl.classList.remove('is-open');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-open');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-open'), 4000);
  };

  /* -- the buttons --------------------------------------------------------- */
  (() => {
    const buttons = $$('[data-interest]');

    // The offer list's own rows. Adding a coffee changed one chip inside one
    // cell, so a buyer working down a table of nine lots had no way to see
    // which ones were already in the cart without reading every cell. The row
    // itself carries the state now: light green, with a forest rule down the
    // left. A row is in the cart if either of its two markets is.
    const rows = $$('.offer-row');
    const paintRows = () => {
      if (!rows.length) return;
      const list = readList();
      rows.forEach((tr) => {
        const on = $$('[data-interest]', tr).some((b) => {
          try { return list.some((x) => x.id === JSON.parse(b.dataset.interest).id); }
          catch { return false; }
        });
        tr.classList.toggle('is-in-cart', on);
      });
    };

    const paint = (btn) => {
      const payload = JSON.parse(btn.dataset.interest);
      const on = readList().some((x) => x.id === payload.id);
      const small = btn.className.includes('gap-1.5');
      btn.setAttribute('aria-pressed', String(on));
      if (small) {
        btn.className =
          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-wider transition-colors ' +
          (on ? 'border-amber bg-amber text-forest' : 'border-line text-forest hover:border-amber');
        btn.innerHTML = (on ? ICON_CHECK : ICON_CART).replace('SIZE', 'h-3 w-3') + (on ? 'In Cart' : 'Add to Cart');
      } else {
        btn.className =
          'inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ' +
          (on ? 'bg-amber text-forest' : 'bg-forest text-paper hover:bg-amber hover:text-forest');
        btn.innerHTML = (on ? ICON_CHECK : ICON_CART).replace('SIZE', 'h-4 w-4') + (on ? 'Added to Cart' : 'Add to Cart');
      }
    };

    buttons.forEach((btn) => {
      paint(btn);
      btn.addEventListener('click', () => {
        const payload = JSON.parse(btn.dataset.interest);
        const list = readList();
        const i = list.findIndex((x) => x.id === payload.id);
        const adding = i < 0;
        if (adding) list.push(payload); else list.splice(i, 1);
        writeList(list);
        buttons.forEach(paint);
        paintRows();
        paintCart();
        toast(
          adding ? `${payload.skuName} added to your cart` : `${payload.skuName} removed`,
          list.length ? `View cart (${list.length})` : '',
        );
      });
    });

    paintRows();
    paintCart();
    // Two tabs open on the offer list is a normal way to compare lots; keep
    // the badge honest in the one that is not being clicked.
    window.addEventListener('storage', (e) => {
      if (e.key && e.key !== KEY) return;
      buttons.forEach(paint);
      paintRows();
      paintCart();
    });
  })();

  /* ------------------------------------------------------ 10. contact form */
  (() => {
    // Only the contact form. A bare 'main form' also caught the shop's
    // Add to cart and cart forms and turned every purchase into an email
    // (found 14 Oct 2026).
    const form = $('main form.cf-form');
    if (!form) return;
    const lot = new URLSearchParams(location.search).get('lot');

    // ?lot= prefills the message, as in the original. The WhatsApp deep link
    // it used to rewrite is gone with the rest of the phone numbers.
    if (lot) {
      const message = $('#message', form);
      if (message) message.value = `I would like to request a sample of ${lot}.`;
    }

    // The original's handler was `e.preventDefault(); setSent(true)` — it swapped
    // in a thank-you panel and sent nothing, so every enquiry was lost silently
    // (README, "Known gaps" 1). That was survivable while the footer printed an
    // address and two numbers. It is not now that the form is the only way in,
    // so it composes the mail itself, the same way the offer-list cart does.
    const NL = '\n';
    const value = (name) => {
      const el = form.elements[name];
      return el && el.value ? String(el.value).trim() : '';
    };

    form.addEventListener('submit', (e) => {
      e.preventDefault();

      const lines = [
        'Hello Hoysala,',
        '',
        value('message') || 'I would like to enquire about your coffee.',
        '',
        `Name: ${value('name')}`,
        `Company: ${value('company')}`,
        `Country: ${value('country')}`,
        `Interest: ${value('interest')}`,
        `Email: ${value('email')}`,
        '',
        'Thank you.',
      ];

      const subject = `Enquiry — ${value('interest') || 'Coffee'}${value('company') ? ` — ${value('company')}` : ''}`;
      window.location.href =
        `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join(NL))}`;

      const panel = document.createElement('div');
      panel.className = 'reveal reveal-in cf-sent';
      panel.innerHTML =
        '<h2 class="text-2xl text-forest">Check your email app</h2>' +
        '<p class="mt-3 text-sm leading-relaxed text-ink-2">Your enquiry is written and waiting in a new message. Press send there and it reaches us. We usually reply within two working days.</p>';
      form.replaceWith(panel);
    });
  })();

  /* ----------------------------------------------------- 11. enquiry page */
  (() => {
    const main = $('main');
    // Static build serves this at /enquiry/ (or /enquiry/index.html); the
    // Shopify theme serves the same template at /pages/enquiry, with no
    // trailing slash. Matching on "/enquiry/" only caught the static one, so
    // on the live storefront this block bailed and the cart page rendered as
    // a heading over an empty section — not even the empty-cart copy. Compare
    // the last path segment instead. The per-lot pages are ".../-enquire",
    // not "enquiry", so nothing else matches.
    const route = location.pathname.replace(/\/index\.html$/, '').replace(/\/+$/, '');
    if (!main || !/(?:^|\/)enquiry$/.test(route)) return;
    const host = $('section > div', main);
    if (!host) return;

    // Both halves of the cart send email now. This used to open WhatsApp on
    // +91 94487 81800 for the India list and compose a mail for the EU one;
    // Rahul took every phone number off the site on 22 Sep 2026, so the only
    // difference left between the two is what the message asks the buyer for.
    const EMAIL = CONTACT_EMAIL;
    const BLURB = {
      india: 'Available for shipping within India — cured against order and dispatched from Chikmagalur to your roastery.',
      international: 'Available for export from India to the EU — shipped from Indian port to your European port after sample approval.',
    };
    const TRASH = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash-2 h-4 w-4" aria-hidden="true"><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path><path d="M3 6h18"></path><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>';

    const message = (market, items) => {
      const lines = items.map((it, i) =>
        `${i + 1}. ${it.skuName} — ${it.process}, ${it.certification}. Quantity required: ____ bags`);
      return `Hello Hoysala,\n\nI am interested in the following coffees ${
        market === 'india' ? 'for shipping within India' : 'for export from India to the EU'
      }:\n\n${lines.join('\n')}\n\nCompany: \n${
        market === 'india' ? 'Delivery city' : 'Destination port'
      }: \n\nThank you.`;
    };

    const card = (market, items) => {
      const india = market === 'india';
      const body = message(market, items);
      const subject = india
        ? 'Coffee enquiry — shipping within India'
        : 'Coffee enquiry — export to the EU';
      const href = `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      return (
        '<div class="rounded-lg border border-line bg-paper">' +
          '<div class="border-b border-line px-6 py-5 text-center">' +
            `<h2 class="font-display text-2xl text-forest">${india ? 'Shipping Within India' : 'Export From India to the EU'}</h2>` +
            `<p class="mt-2 text-xs leading-relaxed text-ink-2">${BLURB[market]}</p>` +
          '</div>' +
          '<ul class="divide-y divide-line">' +
            items.map((it) =>
              '<li class="flex items-start justify-between gap-4 px-6 py-4">' +
                '<div>' +
                  `<span class="block text-sm text-forest">${it.skuName}</span>` +
                  `<span class="mt-1 block text-xs text-ink-2">${it.process} · ${it.bagsAvailable} ${it.bagsAvailable === 1 ? 'bag' : 'bags'} available</span>` +
                '</div>' +
                `<button type="button" data-remove="${it.id}" aria-label="Remove ${it.skuName}" class="shrink-0 text-ink-2 transition-colors hover:text-amber">${TRASH}</button>` +
              '</li>').join('') +
          '</ul>' +
          '<div class="px-6 py-6 text-center">' +
            `<a href="${href}" class="inline-flex rounded-full bg-forest px-6 py-3 text-sm font-semibold text-paper transition-colors hover:bg-amber hover:text-forest">Send by Email</a>` +
            '<p class="mt-3 text-xs text-ink-2">Opens in your email app, already written.</p>' +
          '</div>' +
        '</div>'
      );
    };

    const render = () => {
      const list = readList();
      // The captured SSR left this slot empty — React only ever rendered the
      // list into it on the client, so a first-time visitor arriving from the
      // header cart met a page with a heading and nothing under it.
      if (!list.length) {
        host.innerHTML =
          '<div class="reveal reveal-in mx-auto max-w-xl text-center">' +
            '<p class="text-sm leading-relaxed text-ink-2">Your cart is empty. Open an offer list, add every coffee you want quoted, then come back here — we will split the list by market and compose one enquiry for each.</p>' +
            '<div class="mt-8 flex flex-wrap justify-center gap-4">' +
              `<a href="${pageUrl('coffee/athi')}" class="hoysala-cart-cta">Browse the Offer List</a>` +
              `<a href="${pageUrl('contact')}" class="inline-flex items-center rounded-full border border-line px-6 py-3 text-sm text-forest transition-colors hover:border-forest">Contact Us Instead</a>` +
            '</div>' +
          '</div>';
        return;
      }
      const india = list.filter((x) => x.market === 'india');
      const intl = list.filter((x) => x.market === 'international');
      host.innerHTML =
        '<div class="reveal reveal-in">' +
          // hoysala-cart-markets is the CSS hook for the desktop width of this
          // pair (site-overrides.css §20.14). The Tailwind classes beside it
          // still set the mobile and tablet layout.
          '<div class="hoysala-cart-markets mx-auto grid max-w-4xl gap-6 md:grid-cols-2">' +
            (india.length ? card('india', india) : '') +
            (intl.length ? card('international', intl) : '') +
          '</div>' +
          '<div class="mt-10 flex flex-wrap justify-center gap-4 text-sm">' +
            `<a href="${pageUrl('coffee/athi')}" class="text-ink-2 transition-colors hover:text-amber">← Add More Coffees</a>` +
            '<button type="button" data-clear class="text-ink-2 transition-colors hover:text-amber">Clear List</button>' +
          '</div>' +
        '</div>';
      $$('[data-remove]', host).forEach((btn) => btn.addEventListener('click', () => {
        writeList(readList().filter((x) => x.id !== btn.dataset.remove));
        location.reload();
      }));
      const clear = $('[data-clear]', host);
      if (clear) clear.addEventListener('click', () => { writeList([]); location.reload(); });
    };
    render();
  })();

  /* ------------------------------ 8b. returning to the door you came through */
  // "Change Selection" on an offer list carries ?cert=, so coming back lands on
  // the certification you actually picked. On a phone the three doors are one
  // horizontal row, so the right one has to be scrolled back into view.
  (() => {
    const row = $('[data-cert-chooser]');
    if (!row) return;
    const want = new URLSearchParams(location.search).get('cert');
    if (!want) return;
    const card = $$(':scope > div', row).find((d) => {
      const a = $('a', d);
      return a && new RegExp(`[?&]cert=${want}(?:&|$)`).test(a.getAttribute('href') || '');
    });
    if (!card) return;
    // block:'nearest' so it only moves the row sideways, not the whole page
    card.scrollIntoView({ block: 'nearest', inline: 'center' });
  })();

  /* ------------------------------------------------ 9. the roasters marquee */
  // Driving this by writing scrollLeft on every animation frame was a mistake.
  // Each write lands on the main thread, forces the browser to reconcile it
  // against its own scrolling machinery, and rounds to whole pixels — so at
  // roughly one pixel per frame the row crawled and stuttered on a phone.
  //
  // Instead the drift is a Web Animations transform on the track, which the
  // compositor runs by itself: smooth regardless of what the main thread is
  // doing, and free while it is off screen. Grabbing the row is then just a
  // matter of moving the animation's clock rather than a scroll position —
  // pause on touch, scrub with the finger, play on release. No scroll events,
  // so nothing to fight.
  //
  // Three things then decide whether the grab feels immediate, and all three
  // are below rather than in the animation: the track is measured once and on
  // resize instead of inside every move (that measure forced a layout flush per
  // event), the clock is seeked once per frame instead of once per event, and
  // letting go hands the finger's speed to the clock and decays it back to the
  // drift instead of stopping dead.
  //
  // The markup already carries the card set twice (the second copy is
  // aria-hidden) because the CSS version needed it to loop; translating by
  // exactly -50% therefore lands on identical pixels and the wrap is invisible.
  (() => {
    const track = $('.marquee-track');
    if (!track) return;
    const view = track.parentElement;
    if (!view) return;
    if (!track.animate) return; // no WAAPI: leave the CSS keyframes running

    view.setAttribute('data-marquee-viewport', '');
    view.setAttribute('data-marquee-live', '');

    // The CSS lap time stays the source of truth for pace, less 15%.
    const cssSeconds = parseFloat(getComputedStyle(track).animationDuration) || 60;
    const lapMs = (cssSeconds / 1.15) * 1000;

    const drift = track.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(-50%)' }],
      { duration: lapMs, iterations: Infinity, easing: 'linear' },
    );

    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (still.matches) drift.pause();

    const holds = new Set();
    const apply = () => {
      if (holds.size || still.matches) drift.pause();
      else drift.play();
    };
    const hold = (why) => { holds.add(why); apply(); };
    const release = (why) => { holds.delete(why); apply(); };

    const now = () => performance.now();
    // Keep the clock positive and inside one lap; the visual is identical.
    const wrap = (t) => ((t % lapMs) + lapMs) % lapMs;
    const clock = () => Number(drift.currentTime) || 0;

    // --- geometry -----------------------------------------------------------
    // One set is half the track. Moving the finger by dx should move the row by
    // dx, so the clock shifts by that fraction of a lap.
    //
    // This used to be measured inside every pointer move, and
    // getBoundingClientRect() forces the browser to flush pending layout before
    // it can answer. On a page this full of :has() selectors that is the
    // expensive kind of flush, and it was happening once per move event — which
    // is most of why the grab felt like dragging through treacle. The width
    // only changes when something resizes, so measure it there instead. The
    // observer also covers the lazy-loaded card images settling in, which the
    // old on-demand measure got for free and this would otherwise miss.
    let half = 0;
    const measure = () => { half = track.getBoundingClientRect().width / 2; };
    measure();
    if (window.ResizeObserver) new ResizeObserver(measure).observe(track);
    else window.addEventListener('resize', measure);

    // --- the glide ----------------------------------------------------------
    // Letting go used to drop the row straight back to a 52-second lap, so a
    // flick travelled exactly as far as the finger did and no further: you
    // threw it and it just stopped. Instead the finger's parting speed becomes
    // the clock's speed and decays back to the drift, which is what makes the
    // row feel like it has weight rather than lag.
    // Decay per 60Hz frame, blending toward the drift rate: 0.94 gives a ~270ms
    // time constant, so the throw is spent in about three quarters of a second.
    const GLIDE_DECAY = 0.94;
    // Ceiling on the finger speed the throw is built from, in px/ms. Clamping
    // the speed rather than the resulting clock rate keeps this a guard against
    // a freak sample instead of a limit on how far a hard flick may travel —
    // the rate ceiling it replaced depended on the track width, so it silently
    // capped the throw at about a card and a half.
    const MAX_FLICK = 4;
    let glideRaf = 0;

    // Always hands 'drag' back, so whoever cancels it doesn't have to.
    const cancelGlide = () => {
      if (!glideRaf) return;
      cancelAnimationFrame(glideRaf);
      glideRaf = 0;
      release('drag');
    };

    // rate is in clock-ms per real-ms; the drift's own rate is exactly 1, so
    // decaying toward 1 covers a throw with the drift and against it alike.
    const glide = (rate) => {
      let t = clock();
      let last = now();
      const step = (frame) => {
        const dt = Math.min(frame - last, 64); // a backgrounded tab mustn't teleport it
        last = frame;
        rate = 1 + (rate - 1) * Math.pow(GLIDE_DECAY, dt / 16.667);
        t = wrap(t + rate * dt);
        drift.currentTime = t;
        if (Math.abs(rate - 1) < 0.03) {
          glideRaf = 0;
          release('drag'); // back to the compositor from here
          return;
        }
        glideRaf = requestAnimationFrame(step);
      };
      glideRaf = requestAnimationFrame(step);
    };

    // --- dragging -----------------------------------------------------------
    let dragging = false;
    let startX = 0;
    let startTime = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;   // px per ms of finger travel, smoothed
    let pendingX = null;
    let moveRaf = 0;

    // Land whatever position is waiting in pendingX, if any.
    const applyMove = () => {
      moveRaf = 0;
      if (pendingX === null || half <= 0) return;
      drift.currentTime = wrap(startTime - ((pendingX - startX) / half) * lapMs);
      pendingX = null;
    };

    const beginDrag = (x) => {
      cancelGlide();
      dragging = true;
      startX = x;
      lastX = x;
      lastT = now();
      velocity = 0;
      startTime = clock();
      if (half <= 0) measure();
      hold('drag');
    };

    const moveDrag = (x) => {
      if (!dragging) return;
      const t = now();
      const dt = t - lastT;
      if (dt > 0) {
        const v = (x - lastX) / dt;
        // Weight the newest sample heavily — a flick is decided in its last few
        // milliseconds — but keep enough history to ride out one bad reading.
        // A long gap means the finger had stopped, so start the average over.
        velocity = dt > 120 ? v : velocity * 0.4 + v * 0.6;
        lastX = x;
        lastT = t;
      }
      pendingX = x;
      // One seek per frame. Pointer moves arrive faster than frames on a 120Hz
      // screen or a high-rate mouse, and every extra seek is work thrown away
      // before it ever reaches the glass.
      if (!moveRaf) moveRaf = requestAnimationFrame(applyMove);
    };

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      if (moveRaf) {
        cancelAnimationFrame(moveRaf);
        moveRaf = 0;
      }
      // The last move can still be sitting in pendingX when the finger lifts;
      // landing it first stops the glide starting from a stale position.
      applyMove();
      if (still.matches || half <= 0) {
        release('drag');
        return;
      }
      const v = Math.max(-MAX_FLICK, Math.min(MAX_FLICK, velocity));
      glide(-(v / half) * lapMs);
    };

    view.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      beginDrag(e.clientX);
      // Without capture the row freezes the moment the pointer leaves it
      // mid-drag, which reads as a stall rather than a boundary.
      try { view.setPointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
    }, { passive: true });
    view.addEventListener('pointermove', (e) => moveDrag(e.clientX), { passive: true });
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    if (!window.PointerEvent) {
      view.addEventListener('touchstart', (e) => beginDrag(e.touches[0].clientX), { passive: true });
      view.addEventListener('touchmove', (e) => moveDrag(e.touches[0].clientX), { passive: true });
      view.addEventListener('touchend', endDrag, { passive: true });
      view.addEventListener('touchcancel', endDrag, { passive: true });
    }

    // Hover-to-pause only where there is a real hovering pointer. Binding it on
    // a touch screen leaves a hold that never releases.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      view.addEventListener('mouseenter', () => hold('hover'));
      view.addEventListener('mouseleave', () => release('hover'));
    }

    // Horizontal wheel or trackpad scrubbing, then pick back up. Same two fixes
    // as the drag: no per-event measure, and the deltas for a frame are summed
    // and seeked once.
    let wheelTimer = 0;
    let wheelDelta = 0;
    let wheelRaf = 0;

    const applyWheel = () => {
      wheelRaf = 0;
      if (half <= 0) return;
      drift.currentTime = wrap(clock() + (wheelDelta / half) * lapMs);
      wheelDelta = 0;
    };

    view.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // vertical: let the page have it
      cancelGlide();
      hold('wheel');
      wheelDelta += e.deltaX;
      if (!wheelRaf) wheelRaf = requestAnimationFrame(applyWheel);
      // The timer only fires once the stream truly stops — trackpad inertia
      // keeps resetting it — so this is the pause after scrubbing ends, not a
      // delay during it.
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => release('wheel'), 260);
    }, { passive: true });

    still.addEventListener('change', apply);
  })();

  /* ------------------------------------------- 13. brochure specification */
  //
  // The four figures at the top of a SKU's spec sheet, animated exactly as the
  // home page's "36+ farms / 10,000+ bags" pair is — same 1,400ms, same
  // ease-out cubic, same 150ms stagger — because they are deliberately the
  // same device and a different timing would read as a different component.
  //
  // The pending attribute is set from script, not markup, so a reader with JS
  // disabled sees the finished numbers rather than four invisible tiles.
  (() => {
    const host = $('[data-spec-stats]');
    if (!host) return;
    const tiles = $$('.spec-stat', host);
    if (!tiles.length) return;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (still) return;

    host.setAttribute('data-stats-pending', '');
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const countTo = (el, target) => {
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / 1400, 1);
        el.textContent = Math.round(target * easeOutCubic(p)).toLocaleString();
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const run = () => {
      host.removeAttribute('data-stats-pending');
      tiles.forEach((tile, i) => {
        tile.style.transitionDelay = `${i * 150}ms`;
        tile.style.opacity = '1';
        tile.style.transform = 'none';
        const value = $('.spec-stat-value', tile);
        const figure = $('.spec-stat-figure', tile);
        // Altitude is a range and carries no data-count-to; it just fades in.
        if (value && figure && value.dataset.countTo) countTo(figure, Number(value.dataset.countTo));
      });
    };

    const obs = new IntersectionObserver((entries, o) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        run();
        o.disconnect();
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });
    obs.observe(host);
  })();

  /* ------------------------------------ 14. home section bar (2026-09-17) */
  // The bar under the home hero (Artisan Editorial) marks the section being
  // read. On a phone the bar is a sideways scroller, so the marked link is
  // also brought into view within it.
  (() => {
    const bar = $('.hx-jumpbar');
    if (!bar) return;
    const row = $('.hx-jumpbar__row', bar);
    const links = $$('a[data-jump]', bar);
    const targets = links.map((a) => document.getElementById(a.hash.slice(1))).filter(Boolean);
    if (!targets.length) return;
    const mark = (id) => {
      for (const a of links) {
        const on = a.hash === `#${id}`;
        a.classList.toggle('is-active', on);
        if (on && row.scrollWidth > row.clientWidth) {
          row.scrollTo({ left: a.offsetLeft - 20, behavior: 'smooth' });
        }
      }
    };
    const spy = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) mark(e.target.id);
    }, { rootMargin: '-45% 0px -50% 0px' });
    targets.forEach((t) => spy.observe(t));
  })();

  // §15 Our Story: the journey rail. One step open at a time. Click or tap
  // anywhere, and on a mouse the step under the pointer opens as you move
  // down the rail — Rahul asked for the detail to come up on hover. Touch
  // and keyboard both fall back to the click, so nothing depends on hover.
  (() => {
    const steps = $$('.hx-journey__steps:not(.is-static) .hx-step');
    if (!steps.length) return;

    const open = (step) => {
      for (const s of steps) {
        const on = s === step;
        s.classList.toggle('is-open', on);
        const btn = $('.hx-step__head', s);
        if (btn) btn.setAttribute('aria-expanded', on ? 'true' : 'false');
      }
    };

    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    let hoverTimer = null;

    for (const step of steps) {
      const btn = $('.hx-step__head', step);
      if (!btn) continue;

      btn.addEventListener('click', () => {
        clearTimeout(hoverTimer);
        open(step.classList.contains('is-open') ? null : step);
      });

      btn.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !fine.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => open(step), 130);
      });

      btn.addEventListener('pointerleave', () => clearTimeout(hoverTimer));
    }
  })();

  observeReveals();
})();
